<template>
    <el-dialog
        title="Why this worker is flagged"
        width="640px"
        :close-on-click-modal="false"
        custom-class="hts-blocking-modal"
        :visible.sync="visible"
        @close="$emit('close')"
        data-testid="hts-details-dialog">

        <!-- The blockers, named. This is the whole point of the issue: the eyeball
             used to say "Not available for HTS" and take the reason away. -->
        <div class="hts-blockers" data-testid="hts-details-blockers">
            <span v-for="b in blockers" :key="b.code" class="hts-blocker-chip" :data-testid="'hts-details-blocker-' + b.code">
                {{ b.code }} — {{ b.label }}
            </span>
            <span v-if="!blockers.length" data-testid="hts-details-blocker-none">Flagged across this run.</span>
        </div>

        <p class="hts-window" v-if="windowRange" data-testid="hts-details-window">
            Checked across the whole run: {{ windowRange.start }} to {{ windowRange.end }}.
        </p>

        <div v-loading="loading">
            <!-- Recurring unavailability. Shown AS STORED, for the whole week, on
                 purpose: FS16476 is only self-evident when the dispatcher can see
                 that the windows are overnight and the calls are not. -->
            <h4 class="hts-section-head">Recurring unavailability</h4>
            <table class="hts-detail-table" v-if="details.recurring && details.recurring.length" data-testid="hts-details-ru">
                <tr v-for="(r, i) in details.recurring" :key="'ru' + i" data-testid="hts-details-ru-row">
                    <td class="hts-day">{{ r.day_name }}</td>
                    <td>{{ r.start }} – {{ r.end }}</td>
                </tr>
            </table>
            <p class="hts-none" v-else data-testid="hts-details-ru-none">None recorded.</p>

            <h4 class="hts-section-head">Vacation</h4>
            <table class="hts-detail-table" v-if="details.vacation && details.vacation.length" data-testid="hts-details-vacation">
                <tr v-for="(v, i) in details.vacation" :key="'v' + i" data-testid="hts-details-vacation-row">
                    <td>{{ v.start }}</td>
                    <td>to {{ v.end }}</td>
                </tr>
            </table>
            <p class="hts-none" v-else data-testid="hts-details-vacation-none">None recorded.</p>

            <h4 class="hts-section-head">Other jobs booked</h4>
            <table class="hts-detail-table" v-if="details.bookings && details.bookings.length" data-testid="hts-details-bookings">
                <tr v-for="(b, i) in details.bookings" :key="'b' + i" :data-testid="'hts-details-booking-' + b.scope">
                    <!-- A booking at another local names the local and NOTHING
                         else (#1821, extended to this surface by ed 2026-09-10).
                         There is nothing more to render, and there must not be. -->
                    <td colspan="2" v-if="b.scope !== 'local'">{{ scopeLabel(b) }}</td>
                    <template v-else>
                        <td class="hts-job">{{ jobLabel(b) }}</td>
                        <td>{{ b.start }} – {{ b.end }}</td>
                    </template>
                </tr>
            </table>
            <p class="hts-none" v-else data-testid="hts-details-bookings-none">None recorded.</p>
        </div>

        <span slot="footer">
            <el-button @click="$emit('clear')" data-testid="hts-details-clear">Clear</el-button>
            <el-button type="primary" @click="$emit('assign')" data-testid="hts-details-assign">{{ assignLabel }}</el-button>
        </span>
    </el-dialog>
</template>

<script>
    /**
     * #1780 — the review popup behind a FAILED HTS eyeball.
     *
     * ed, 2026-09-09: *"the pass is ok, but when the eyeball fails, i want details
     * in the popup with a clear button."*
     *
     * PRESENTATION ONLY. It renders what the server sent and decides nothing: no
     * overlap maths, no verdict, no re-derivation of the flag. The dispatcher reads
     * the stored facts and makes the call, which is the whole point — a flag can be
     * a real conflict or an artefact of the whole-run envelope, and only a human
     * looking at the data can tell.
     *
     * It also OWNS NO ACTION. `assign` and `clear` are emitted, and the host page
     * decides what proceeding means there — an immediate assign on the single page,
     * ticking the checkbox on the bulk page. Wiring an assign in here would put a
     * second assign path beside the one each page already guards.
     */
    import { externalBookingLabel } from '../../../htsBlockers';

    export default {
        name: 'htsBlockingDetails',
        props: {
            visible:     { type: Boolean, default: false },
            loading:     { type: Boolean, default: false },
            // [{code, label}] — from the server, which derives them from the same
            // booleans that produced the verdict. Never re-derived here: the
            // frontend guessing which badge to show is the original defect (#1661
            // records it rendering a BOOKING as "vacation").
            blockers:    { type: Array, default: () => [] },
            details:     { type: Object, default: () => ({ recurring: [], vacation: [], bookings: [] }) },
            windowRange: { type: Object, default: null },
            assignLabel: { type: String, default: 'Assign anyway' },
        },
        methods: {
            // Composed, not rendered whole: the fixed words stay ours and only
            // the client name is data. An orphan (#1604) keeps its own label.
            scopeLabel(b) {
                return b.scope === 'external' ? externalBookingLabel(b) : b.label;
            },
            jobLabel(b) {
                var name = b.job_title || 'Untitled job';
                var num = (b.job_number === null || b.job_number === undefined) ? '' : ' #' + b.job_number;
                var grp = b.group ? ' (' + b.group + ')' : '';
                return name + num + grp;
            },
        },
    };
</script>

<style scoped>
    .hts-blockers { margin-bottom: 8px; }
    .hts-blocker-chip {
        display: inline-block;
        margin: 0 6px 6px 0;
        padding: 2px 8px;
        border-radius: 3px;
        background: #f8bfbf;
        color: #4a1010;
        font-size: 12px;
    }
    .hts-window { color: #777; font-size: 12px; margin: 0 0 12px; }
    .hts-section-head { margin: 14px 0 4px; font-size: 13px; font-weight: 600; }
    .hts-detail-table { width: 100%; font-size: 13px; }
    .hts-detail-table td { padding: 2px 6px 2px 0; vertical-align: top; }
    .hts-day { width: 110px; }
    .hts-job { width: 55%; }
    .hts-none { color: #999; font-size: 13px; margin: 0; }
</style>
